'use client';

import React, { useState, useMemo } from 'react';
import { useLocation, Link as RouterLink } from '@tanstack/react-router';
import { CaretRightIcon } from '@phosphor-icons/react';

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
} from '@/components/ui/sidebar';
import { Badge } from '@/components/ui/badge';

import { RouteItem, RouteItems } from 'src/routes';
import { useIsModuleEnabled } from '@/hooks/organization-config/useOrganizationEnabledModules';
import { useFeatureFlag } from '@/integrations/feature-flags/feature-flags';
import useAuth from '@/api/auth/useAuth';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin } from '@/utils/user';
import {
  useRoutePermissions,
  getPermissionKey,
} from '@/hooks/authz/useRoutePermissions';

// Active icon styling for procurement-demo routes
const PROCUREMENT_DEMO_ACTIVE_BG = '#6a89cc';

function renderIconWithActiveColor(
  icon: React.ReactNode,
  routeKey: string,
  isActive: boolean,
): React.ReactNode {
  // Apply custom styling only to procurement-demo routes when active
  if (
    isActive &&
    routeKey.startsWith('procurement-demo') &&
    React.isValidElement(icon)
  ) {
    const whiteIcon = React.cloneElement(
      icon as React.ReactElement<{ style?: React.CSSProperties }>,
      { style: { color: 'white' } },
    );
    return (
      <span
        style={{
          backgroundColor: PROCUREMENT_DEMO_ACTIVE_BG,
          borderRadius: '6px',
          padding: '4px',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {whiteIcon}
      </span>
    );
  }
  return icon;
}

function isPathActive(base: string | undefined, path: string): boolean {
  if (!base) return false;
  // Normalize trailing slashes except root
  const norm = (s: string) => (s === '/' ? '/' : s.replace(/\/+$/, ''));
  const b = norm(base);
  const p = norm(path);
  if (b === '/') return p === '/';
  return p === b || p.startsWith(`${b}/`);
}

interface NavRoutesProps {
  routes: RouteItems;
}

export function NavRoutes({ routes }: NavRoutesProps) {
  const { isModuleEnabled } = useIsModuleEnabled();
  const { currentUser } = useAuth();
  const { viewMode } = useViewMode();
  const location = useLocation();
  const { pathname } = location;
  const { permissions, isLoading: permissionsLoading } =
    useRoutePermissions(routes);
  const { enabled: showEmailPoMetricsEnabled } = useFeatureFlag(
    'show-email-po-metrics-enabled',
    false,
  );
  const { enabled: apInvoicesUIEnabled } = useFeatureFlag(
    'ap-invoices-ui-enabled',
    false,
  );
  const { enabled: discountValidationEnabled } = useFeatureFlag(
    'discount-validation-enabled',
    false,
  );
  const { enabled: rbacRoutingEnabled } = useFeatureFlag(
    'rbac-routing-enabled',
    false,
  );

  // Map of routes to hide based on feature flags
  const hiddenRoutesMap: Record<string, boolean> = useMemo(() => {
    return {
      'email-po-metrics': !showEmailPoMetricsEnabled,
      'ap-invoices': !apInvoicesUIEnabled,
      'ap-purchase-orders': !apInvoicesUIEnabled,
      'title-accounts-payable': !apInvoicesUIEnabled,
      discounts: !discountValidationEnabled,
    };
  }, [
    showEmailPoMetricsEnabled,
    apInvoicesUIEnabled,
    discountValidationEnabled,
  ]);

  const [openCollapse, setOpenCollapse] = useState<string | boolean>(false);

  // Filter routes based on hidden status, module enablement, permissions, and admin access
  const filteredRoutes = useMemo(() => {
    const userIsAdmin = isAdmin(currentUser, viewMode);
    return routes.filter(
      (route) =>
        !route.hidden &&
        !hiddenRoutesMap[route.key] &&
        (!route.adminOnly || userIsAdmin) &&
        // Module checks
        ((route.moduleNamesAnyOf?.length ?? 0) === 0 ||
          route.moduleNamesAnyOf?.some((module) => isModuleEnabled(module))) &&
        ((route.moduleNamesAllOf?.length ?? 0) === 0 ||
          route.moduleNamesAllOf?.every((module) => isModuleEnabled(module))) &&
        // Permission checks - skip entirely if rbac-routing-enabled flag is off
        // When flag is on, show all routes while loading to prevent flash
        (!rbacRoutingEnabled ||
          permissionsLoading ||
          (((route.permissionsAnyOf?.length ?? 0) === 0 ||
            route.permissionsAnyOf?.some((p) =>
              permissions.get(getPermissionKey(p.action, p.subject)),
            )) &&
            ((route.permissionsAllOf?.length ?? 0) === 0 ||
              route.permissionsAllOf?.every((p) =>
                permissions.get(getPermissionKey(p.action, p.subject)),
              )))),
    );
  }, [
    routes,
    isModuleEnabled,
    hiddenRoutesMap,
    currentUser,
    viewMode,
    permissions,
    permissionsLoading,
    rbacRoutingEnabled,
  ]);

  // Group routes by sections (titles)
  const groupedRoutes = useMemo(() => {
    const groups: Array<{ title?: string; routes: RouteItem[] }> = [];
    let currentGroup: RouteItem[] = [];
    let currentTitle: string | undefined = undefined;

    filteredRoutes.forEach((route) => {
      if (route.type === 'title') {
        // Save previous group if it has items
        if (currentGroup.length > 0) {
          groups.push({ title: currentTitle, routes: currentGroup });
        }
        // Start new group
        currentTitle = route.title;
        currentGroup = [];
      } else if (route.type === 'divider') {
        // End current group and add separator
        if (currentGroup.length > 0) {
          groups.push({ title: currentTitle, routes: currentGroup });
          currentGroup = [];
          currentTitle = undefined;
        }
        groups.push({ title: 'separator', routes: [] });
      } else if (route.type === 'collapse') {
        currentGroup.push(route);
      }
    });

    // Add final group
    if (currentGroup.length > 0) {
      groups.push({ title: currentTitle, routes: currentGroup });
    }

    return groups;
  }, [filteredRoutes]);

  const renderNestedRoutes = (nestedRoutes: RouteItem[]) => {
    return nestedRoutes.map((subRoute) => (
      <SidebarMenuSubItem key={subRoute.key}>
        <SidebarMenuSubButton
          asChild
          isActive={isPathActive(subRoute.route, pathname)}
        >
          {subRoute.href ? (
            <a href={subRoute.href} target="_blank" rel="noreferrer">
              {subRoute.icon && <span className="mr-2">{subRoute.icon}</span>}
              <span>{subRoute.name}</span>
            </a>
          ) : (
            <RouterLink to={subRoute.route || '#'}>
              {subRoute.icon && <span className="mr-2">{subRoute.icon}</span>}
              <span>{subRoute.name}</span>
            </RouterLink>
          )}
        </SidebarMenuSubButton>
      </SidebarMenuSubItem>
    ));
  };

  const renderRoute = (route: RouteItem) => {
    const isActive = isPathActive(route.route, pathname);
    const hasCollapse = route.collapse && route.collapse.length > 0;
    const isOpen = openCollapse === route.key;

    if (hasCollapse && !route.noCollapse) {
      // Collapsible route with sub-items
      return (
        <Collapsible
          key={route.key}
          open={isOpen}
          onOpenChange={(open) => setOpenCollapse(open ? route.key : false)}
        >
          <SidebarMenuItem>
            <CollapsibleTrigger asChild>
              <SidebarMenuButton tooltip={route.name} isActive={isActive}>
                {renderIconWithActiveColor(route.icon, route.key, isActive)}
                <span>{route.name}</span>
                {route.badge && (
                  <Badge
                    variant={
                      route.badge.color === 'primary' ? 'default' : 'secondary'
                    }
                    className="ml-auto"
                  >
                    {route.badge.label}
                  </Badge>
                )}
                <CaretRightIcon className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
              </SidebarMenuButton>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <SidebarMenuSub>
                {route.collapse && renderNestedRoutes(route.collapse)}
              </SidebarMenuSub>
            </CollapsibleContent>
          </SidebarMenuItem>
        </Collapsible>
      );
    }

    // Simple route (no collapse or noCollapse = true)
    return (
      <SidebarMenuItem key={route.key}>
        <SidebarMenuButton asChild tooltip={route.name} isActive={isActive}>
          {route.href ? (
            <a href={route.href} target="_blank" rel="noreferrer">
              {renderIconWithActiveColor(route.icon, route.key, isActive)}
              <span>{route.name}</span>
              {route.badge && (
                <Badge
                  variant={
                    route.badge.color === 'primary' ? 'default' : 'secondary'
                  }
                  className="ml-auto"
                >
                  {route.badge.label}
                </Badge>
              )}
            </a>
          ) : (
            <RouterLink to={route.route || '#'}>
              {renderIconWithActiveColor(route.icon, route.key, isActive)}
              <span>{route.name}</span>
              {route.badge && (
                <Badge
                  variant={
                    route.badge.color === 'primary' ? 'default' : 'secondary'
                  }
                  className="ml-auto"
                >
                  {route.badge.label}
                </Badge>
              )}
            </RouterLink>
          )}
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  };

  return (
    <>
      {groupedRoutes.map((group, index) => {
        if (group.title === 'separator') {
          return <SidebarSeparator key={`separator-${index}`} />;
        }

        if (group.routes.length === 0) {
          return null;
        }

        return (
          <SidebarGroup key={group.title || `group-${index}`}>
            {group.title && (
              <SidebarGroupLabel>{group.title}</SidebarGroupLabel>
            )}
            <SidebarMenu>{group.routes.map(renderRoute)}</SidebarMenu>
          </SidebarGroup>
        );
      })}
    </>
  );
}
